import { useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import {
  glFilters,
  brightness as glBrightness,
  contrast as glContrast,
  saturate,
  invert,
  sharpen,
  customShader,
  blur,
} from "webgl-filters";
import { Transformation } from "./types";
import { generateMaskFromImage } from "./generateMask";
import { resolveSpecsImageUrl } from "./specsAssetUrls";

interface FilterParams {
  brightness: number;
  contrast: number;
  saturation: number;
  hasSharpen: boolean;
  hasInvert: boolean;
  hasAutoMask: boolean;
  hasAlphaToMask: boolean;
  hasWhiteToMask: boolean;
  hasBlackToMask: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function glResultToDataUrl(result: any): string {
  const canvas = document.createElement("canvas");
  canvas.width = result.width;
  canvas.height = result.height;
  const ctx = canvas.getContext("2d")!;
  const imgData = ctx.createImageData(result.width, result.height);
  imgData.data.set(new Uint8ClampedArray(result.data));
  ctx.putImageData(imgData, 0, 0);
  return canvas.toDataURL("image/png");
}

function readBlobAsDataUrl(blob: Blob, label: string) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }

      reject(new Error(`Failed to read image "${label}".`));
    };
    reader.onerror = () =>
      reject(new Error(`Failed to read image "${label}".`));
    reader.readAsDataURL(blob);
  });
}

async function resolveImageProcessingInputUrl(url: string) {
  if (url.startsWith("data:") || url.startsWith("blob:")) {
    return url;
  }

  const resolvedUrl = resolveSpecsImageUrl(url);
  const response = await fetch(resolvedUrl);
  if (!response.ok) {
    throw new Error(
      `Failed to read image "${resolvedUrl}": ${response.status} ${response.statusText}`
    );
  }

  return readBlobAsDataUrl(await response.blob(), resolvedUrl);
}

async function processImage(
  url: string,
  params: FilterParams,
  cancelled: () => boolean
): Promise<string> {
  const chain = glFilters();

  // Alpha-to-mask: extract alpha channel as B&W + smooth edges
  if (params.hasAlphaToMask) {
    chain.addFilter(
      customShader({
        source: `
          vec4 color = texture2D(u_texture, v_texCoord);
          float mask = color.a;
          gl_FragColor = vec4(mask, mask, mask, 1.0);
        `,
      })
    );
    chain.addFilter(blur({ radius: 4, strength: 1.0 }));
  }

  // White-to-mask: distance from pure white -> mask + edge vignette dropoff
  if (params.hasWhiteToMask) {
    chain.addFilter(
      customShader({
        source: `
          vec4 color = texture2D(u_texture, v_texCoord);
          float dist = distance(color.rgb, vec3(1.0));
          float mask = smoothstep(0.0, 0.04, dist);
          mask = pow(mask, 3.0);

          // Edge vignette: fade to black near image borders (~1% dropoff)
          vec2 uv = v_texCoord;
          float edgeDist = min(min(uv.x, 1.0 - uv.x), min(uv.y, 1.0 - uv.y));
          float vignette = smoothstep(0.0, 0.01, edgeDist);
          mask *= vignette;

          gl_FragColor = vec4(mask, mask, mask, 1.0);
        `,
      })
    );
    chain.addFilter(blur({ radius: 4, strength: 1.0 }));
    chain.addFilter(
      customShader({
        source: `
          vec4 color = 1.0 - texture2D(u_texture, v_texCoord);
          float dist = distance(color.rgb, vec3(1.0));
          float mask = smoothstep(0.0, 0.2, dist);
          gl_FragColor = vec4(mask, mask, mask, 1.0);
        `,
      })
    );
  }

  // Black-to-mask: dark areas -> black (excluded), light areas -> white
  if (params.hasBlackToMask) {
    chain.addFilter(
      customShader({
        source: `
          vec4 color = texture2D(u_texture, v_texCoord);
          float lum = dot(color.rgb, vec3(0.299, 0.587, 0.114));
          float mask = smoothstep(0.1, 0.2, lum);
          gl_FragColor = vec4(mask, mask, mask, mask);
        `,
      })
    );
    chain.addFilter(blur({ radius: 2, strength: 1.0 }));
  }

  if (params.brightness !== 0) {
    chain.addFilter(glBrightness({ amount: params.brightness / 100 }));
  }
  if (params.contrast !== 0) {
    chain.addFilter(glContrast({ factor: 1 + params.contrast / 100 }));
  }
  if (params.saturation !== 0) {
    chain.addFilter(saturate({ factor: 1 + params.saturation / 100 }));
  }
  if (params.hasSharpen) {
    chain.addFilter(sharpen());
  }
  if (params.hasInvert) {
    chain.addFilter(invert());
  }

  const inputUrl = await resolveImageProcessingInputUrl(url);
  if (cancelled()) {
    return inputUrl;
  }

  const result = await chain.applyAsync(inputUrl);
  let resultUrl = glResultToDataUrl(result);

  if (params.hasAutoMask && !cancelled()) {
    resultUrl = await generateMaskFromImage(resultUrl);
  }

  return resultUrl;
}

/**
 * Applies webgl-filters transformations to a source image data URL.
 * Returns a processed data URL that updates whenever source or transforms change.
 */
export function useImageProcessing(
  sourceImageUrl: string | null,
  transformations: Transformation[]
) {
  const [processedUrl, setProcessedUrl] = useState<string | null>(null);
  const cancelRef = useRef(false);

  const brightness = getParam(
    transformations,
    "adjustBrightness",
    "brightness",
    0
  );
  const contrast = getParam(transformations, "adjustContrast", "contrast", 0);
  const saturation = getParam(
    transformations,
    "adjustSaturation",
    "saturation",
    0
  );
  const hasSharpen = hasTransform(transformations, "sharpen");
  const hasInvert = hasTransform(transformations, "invertColors");
  const hasAutoMask = hasTransform(transformations, "autoMask");
  const hasAlphaToMask = hasTransform(transformations, "alphaToMask");
  const hasWhiteToMask = hasTransform(transformations, "whiteToMask");
  const hasBlackToMask = hasTransform(transformations, "blackToMask");

  const hasManualTransforms =
    brightness !== 0 ||
    contrast !== 0 ||
    saturation !== 0 ||
    hasSharpen ||
    hasInvert ||
    hasAutoMask ||
    hasAlphaToMask ||
    hasWhiteToMask ||
    hasBlackToMask;

  const debouncedProcess = useMemo(
    () =>
      debounce(async (url: string, params: FilterParams) => {
        cancelRef.current = false;
        try {
          const resultUrl = await processImage(
            url,
            params,
            () => cancelRef.current
          );
          if (!cancelRef.current) {
            setProcessedUrl(resultUrl);
          }
        } catch (err) {
          console.error("[useImageProcessing] Filter error:", err);
          if (!cancelRef.current) {
            setProcessedUrl(url);
          }
        }
      }, 40),
    []
  );

  useEffect(() => {
    if (!sourceImageUrl) {
      setProcessedUrl(null);
      return;
    }

    if (!hasManualTransforms) {
      setProcessedUrl(sourceImageUrl);
      return;
    }

    debouncedProcess(resolveSpecsImageUrl(sourceImageUrl), {
      brightness,
      contrast,
      saturation,
      hasSharpen,
      hasInvert,
      hasAutoMask,
      hasAlphaToMask,
      hasWhiteToMask,
      hasBlackToMask,
    });

    return () => {
      cancelRef.current = true;
      debouncedProcess.cancel();
    };
  }, [
    sourceImageUrl,
    brightness,
    contrast,
    saturation,
    hasSharpen,
    hasInvert,
    hasAutoMask,
    hasAlphaToMask,
    hasWhiteToMask,
    hasBlackToMask,
    hasManualTransforms,
    debouncedProcess,
  ]);

  return processedUrl;
}

function hasTransform(
  transformations: Transformation[],
  type: string
): boolean {
  return transformations.some((tr) => tr.type === type && tr.enabled);
}

function getParam(
  transformations: Transformation[],
  type: string,
  param: string,
  defaultValue: number
): number {
  const t = transformations.find((tr) => tr.type === type && tr.enabled);
  if (!t) return defaultValue;
  const val = t.params[param];
  return typeof val === "number" ? val : defaultValue;
}
